import { type ErrorEvent } from '@sentry/react';

/**
 * Curated allow-list of HTTP responses that `httpClientIntegration` captures as
 * error events but which are *expected* results, not failures - so we drop them
 * in `beforeSend`.
 *
 * `httpClientIntegration` flags any 4xx/5xx response regardless of whether the
 * app treated it as an error, so some entries here are false-positives: a
 * status code (often a 404) that is actually the meaningful negative answer of
 * a lookup the app handles gracefully. These are monitoring noise, not bugs.
 *
 * We deliberately keep wide monitoring (`failedRequestStatusCodes` spanning
 * 400-599) so genuinely unexpected failures are still surfaced. Instead of
 * narrowing that net, we silence individual, well-understood responses here -
 * each with a documented `reason` (and, where possible, a `reference` to the
 * Sentry issue / ticket) so every exception is auditable.
 *
 * To add an exception:
 *   1. Append an entry below with a clear `reason` explaining why the response
 *      is expected and safe to drop (and, if it is a false-positive rather than
 *      a real-but-tolerated failure, say so).
 *   2. Scope it as tightly as you can (method + path + status) so it can't mask
 *      unrelated regressions on the same endpoint.
 *   3. Add a case to `ignoredHttpClientErrors.test.ts`.
 */
export interface IgnoredHttpClientError {
	/** Why this response is expected and safe to drop. Required for auditability. */
	reason: string;
	/** Sentry issue short-id or Jira key for context, when available. */
	reference?: string;
	/** HTTP method(s) this rule applies to (case-insensitive). Omit to match any. */
	methods?: string[];
	/** Status code(s) to drop. Omit to match any captured status. */
	statusCodes?: number[];
	/** Matched against the request URL's pathname. */
	pathPattern: RegExp;
}

export const IGNORED_HTTP_CLIENT_ERRORS: IgnoredHttpClientError[] = [
	{
		reason:
			"Expected negative result, not a failure. profilecard's TeamCentralCardClient fires " +
			'this as part of a deliberate parallel prefetch (constructor preload) to resolve the ' +
			"site's Team Central workspace type ahead of time. A 404 is how the API reports " +
			'"no Atlas/Townsquare workspace here"; the client handles it (returns undefined, so ' +
			'getIsGlobalExperienceWorkspace resolves false). It is a false-positive for ' +
			'httpClientIntegration (which flags every 4xx/5xx), not an app error - but it ' +
			'accounted for ~31k users / ~864k events in 14 days.',
		reference: 'ROVO-EXTENSION-FRONTEND-69N',
		methods: ['GET'],
		statusCodes: [404],
		pathPattern: /\/gateway\/api\/townsquare\/s\/[^/]+\/workspace\/existsWithWorkspaceType$/,
	},
	{
		reason:
			'Expected negative result from the sibling prefetch (organization/containsAnyWorkspace) ' +
			'fired in the same TeamCentralCardClient constructor. A 404 means "this org has no ' +
			'Townsquare workspace" and is handled by getOrgContainsAnyWorkspace (treated as false). ' +
			'Same false-positive class as existsWithWorkspaceType, not an app error.',
		reference: 'ROVO-EXTENSION-FRONTEND-69N',
		methods: ['GET'],
		statusCodes: [404],
		pathPattern: /\/gateway\/api\/townsquare\/s\/[^/]+\/organization\/containsAnyWorkspace$/,
	},
];

const isHttpClientEvent = (event: ErrorEvent): boolean =>
	event.exception?.values?.some((value) => value.mechanism?.type === 'http.client') ?? false;

const getPathname = (url: string | undefined): string | undefined => {
	if (!url) {
		return undefined;
	}
	try {
		return new URL(url).pathname;
	} catch {
		return undefined;
	}
};

/**
 * Returns `true` when the event is an `httpClientIntegration`-captured failure
 * that matches one of the curated {@link IGNORED_HTTP_CLIENT_ERRORS} rules and
 * should therefore be dropped in `beforeSend`.
 */
export const isIgnoredHttpClientError = (event: ErrorEvent): boolean => {
	if (!isHttpClientEvent(event)) {
		return false;
	}

	const pathname = getPathname(event.request?.url);
	if (!pathname) {
		return false;
	}

	const method = event.request?.method?.toUpperCase();
	const status = event.contexts?.response?.status_code;

	return IGNORED_HTTP_CLIENT_ERRORS.some((rule) => {
		if (rule.methods && !(method !== undefined && rule.methods.includes(method))) {
			return false;
		}
		if (rule.statusCodes && !(status !== undefined && rule.statusCodes.includes(status))) {
			return false;
		}
		return rule.pathPattern.test(pathname);
	});
};
